import { generatePalette, getValuesFromPalette } from './palette'

export type Mode = 'light' | 'dark' | 'overall'
export type LightnessRange = 'low' | 'moderate' | 'high' | 'veryHigh'

// Configuration object that defines the logic for each combination
const TOKEN_CONFIG = {
  low: {
    light: {
      background: 1,
      foreground: 16,
      over: 'var(--color-gray-1)',
      popUpBg: 16,
      popUpFg: 1,
      under: 16,
      highlight: 1,
      border: 16,
      accentElement: (base: number) => base,
      accentPrimary: (base: number) => base,
      accentSecondary: (base: number) => base - 1,
      accentTertiary: (base: number) => base - 2,
      accentForeground: 1,
    },
    dark: {
      background: 16,
      foreground: 1,
      over: 1,
      popUpBg: 15,
      popUpFg: 1,
      under: 18,
      highlight: 1,
      border: 1,
      accentElement: 2,
      accentPrimary: 1,
      accentSecondary: 2,
      accentTertiary: 3,
      accentForeground: (base: number) => base,
    },
    overall: {
      background: (base: number) => base,
      foreground: 1,
      over: 1,
      popUpBg: 12,
      popUpFg: 1,
      under: 16,
      highlight: 1,
      border: 16,
      accentElement: 2,
      accentPrimary: 1,
      accentSecondary: 2,
      accentTertiary: 3,
      accentForeground: (base: number) => base,
    },
  },
  moderate: {
    light: {
      background: 1,
      foreground: 16,
      over: 'var(--color-gray-1)',
      popUpBg: 16,
      popUpFg: 1,
      under: 16,
      highlight: 1,
      border: 16,
      accentElement: (base: number) => base,
      accentPrimary: (base: number) => base,
      accentSecondary: (base: number) => base + 1,
      accentTertiary: (base: number) => base + 2,
      accentForeground: 1,
    },
    dark: {
      background: 16,
      foreground: 1,
      over: 1,
      popUpBg: 15,
      popUpFg: 1,
      under: 18,
      highlight: 1,
      border: 1,
      accentElement: 10,
      accentPrimary: (base: number) => base,
      accentSecondary: (base: number) => base + 1,
      accentTertiary: (base: number) => base + 2,
      accentForeground: 1,
    },
    overall: {
      background: (base: number) => base,
      foreground: 1,
      over: 'var(--color-gray-1)',
      popUpBg: 12,
      popUpFg: 1,
      under: 16,
      highlight: 1,
      border: 16,
      accentElement: 1,
      accentPrimary: 1,
      accentSecondary: 1,
      accentTertiary: 2,
      accentForeground: 18,
    },
  },
  high: {
    light: {
      background: 1,
      foreground: 16,
      over: 'var(--color-gray-1)',
      popUpBg: 16,
      popUpFg: 1,
      under: 16,
      highlight: 1,
      border: 16,
      accentElement: 8,
      accentPrimary: (base: number) => base,
      accentSecondary: (base: number) => base + 1,
      accentTertiary: (base: number) => base + 2,
      accentForeground: 18,
    },
    dark: {
      background: 16,
      foreground: 1,
      over: 1,
      popUpBg: 15,
      popUpFg: 1,
      under: 18,
      highlight: 1,
      border: 1,
      accentElement: 2,
      accentPrimary: (base: number) => base,
      accentSecondary: (base: number) => base + 1,
      accentTertiary: (base: number) => base + 2,
      accentForeground: 18,
    },
    overall: {
      background: (base: number) => base,
      foreground: 18,
      over: 'var(--color-gray-1)',
      popUpBg: 12,
      popUpFg: 1,
      under: 16,
      highlight: 1,
      border: 16,
      accentElement: 10,
      accentPrimary: 14,
      accentSecondary: 15,
      accentTertiary: 16,
      accentForeground: (base: number) => base,
    },
  },
  veryHigh: {
    light: {
      background: 1,
      foreground: 16,
      over: 'var(--color-gray-1)',
      popUpBg: 16,
      popUpFg: 1,
      under: 16,
      highlight: 1,
      border: 16,
      accentElement: 8,
      accentPrimary: 15,
      accentSecondary: 16,
      accentTertiary: 17,
      accentForeground: (base: number) => base,
    },
    dark: {
      background: 15,
      foreground: 1,
      over: 1,
      popUpBg: 15,
      popUpFg: 1,
      under: 18,
      highlight: 1,
      border: 1,
      accentElement: (base: number) => base,
      accentPrimary: (base: number) => base,
      accentSecondary: (base: number) => base + 1,
      accentTertiary: (base: number) => base + 2,
      accentForeground: 18,
    },
    overall: {
      background: (base: number) => base,
      foreground: 18,
      over: 'var(--color-gray-1)',
      popUpBg: 12,
      popUpFg: 1,
      under: 16,
      highlight: 1,
      border: 16,
      accentElement: 8,
      accentPrimary: 15,
      accentSecondary: 16,
      accentTertiary: 17,
      accentForeground: (base: number) => base,
    },
  },
} as const

function getLightnessRange(baseL: number): LightnessRange {
  if (baseL <= 43) return 'low'
  if (baseL <= 73) return 'moderate'
  if (baseL <= 94) return 'high'
  return 'veryHigh'
}

function resolveValue(
  base: number,
  get: (index: number) => string,
  value: number | string | ((base: number) => number)
): string {
  if (typeof value === 'string') return value
  if (typeof value === 'function') return get(value(base))
  return get(value)
}

export function getTokens(color: string, mode: Mode) {
  const { base, palette } = generatePalette(color)

  const baseL = base.oklch[0] * 100
  const lightnessRange = getLightnessRange(baseL)

  const config = TOKEN_CONFIG[lightnessRange][mode]
  const get = getValuesFromPalette.bind(null, palette)
  const resolve = resolveValue.bind(null, base.index + 1, get)

  return {
    '--color-background': resolve(config.background),
    '--color-foreground': resolve(config.foreground),
    '--color-over': resolve(config.over),
    '--color-pop-up-bg': resolve(config.popUpBg),
    '--color-pop-up-fg': resolve(config.popUpFg),
    '--color-under': resolve(config.under),
    '--color-highlight': resolve(config.highlight),
    '--color-border': resolve(config.border),
    '--color-accent-element': resolve(config.accentElement),
    '--color-accent-primary': resolve(config.accentPrimary),
    '--color-accent-secondary': resolve(config.accentSecondary),
    '--color-accent-tertiary': resolve(config.accentTertiary),
    '--color-accent-foreground': resolve(config.accentForeground),
  }
}
